import { useCallback } from "react";
import { useWeb3React } from "@web3-react/core";
import useWeb3 from "../useWeb3";
import { chinaContract, chinaContract12 } from "../../utils/contractHelpers";
import Environment from "../../utils/Environment";
import { getTxGasConfig } from "../getTxGasConfig";
import { V4Planner, Actions } from "@uniswap/v4-sdk";
import { RoutePlanner, CommandType } from "@uniswap/universal-router-sdk";

// ─── Constants ────────────────────────────────────────────────────────────────

const ZERO_ADDRESS = Environment.ZERO_ADDRESS;
const POOL_FEES = Environment.POOL_FEES;
const FEE_TO_TICK_SPACING = Environment.FEE_TO_TICK_SPACING;

const COMMANDS = Environment.COMMANDS;

const CHAIN_ADDRESSES = {
  1: {
    usdt: Environment.busd,
    usdc: Environment.usdc,
    weth: Environment.weth,
    gems: Environment.gems,
    v3Factory: Environment.v3Factory,
    v2Factory: Environment.v2Factory,
    v4PoolManager: Environment.v4PoolManager,
    v4StateView: Environment.v4StateView,
  },
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const usePurchaseNFTwithUsdc = () => {
  const { account, chainId } = useWeb3React();
  const web3 = useWeb3();

  const contractType = Environment?.presale?.contractVersion;
  const isFirstContractVersion = contractType === "v0";

  const tokenAddress = Environment.presaleContract;

  const contract = isFirstContractVersion
    ? chinaContract(tokenAddress, web3)
    : chinaContract12(tokenAddress, web3);

  const UsdcAddress = Environment.usdc;

  // ─── Multicall helper ─────────────────────────────────────────────────────────
  // Batch all eth.call into ONE rpc request using Multicall3 (deployed on all chains)

  const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11";

  const multicall = useCallback(
    async (calls) => {
      const aggregate = web3.eth.abi.encodeFunctionCall(
        {
          name: "tryAggregate",
          type: "function",
          inputs: [
            { name: "requireSuccess", type: "bool" },
            {
              name: "calls",
              type: "tuple[]",
              components: [
                { name: "target", type: "address" },
                { name: "callData", type: "bytes" },
              ],
            },
          ],
          outputs: [
            {
              name: "returnData",
              type: "tuple[]",
              components: [
                { name: "success", type: "bool" },
                { name: "returnData", type: "bytes" },
              ],
            },
          ],
        },
        [false, calls],
      );

      const result = await web3.eth.call({
        to: MULTICALL3_ADDRESS,
        data: aggregate,
      });

      const decoded = web3.eth.abi.decodeParameters(
        [
          {
            name: "returnData",
            type: "tuple[]",
            components: [
              { name: "success", type: "bool" },
              { name: "returnData", type: "bytes" },
            ],
          },
        ],
        result,
      );

      return decoded[0];
    },
    [web3],
  );

  // Encode packed V3 path: [addr, uint24, addr]
  const encodeV3Path = useCallback((segments) => {
    let encoded = "0x";
    for (let i = 0; i < segments.length; i++) {
      if (i % 2 === 0) {
        encoded += segments[i]
          .replace("0x", "")
          .toLowerCase()
          .padStart(40, "0");
      } else {
        encoded += segments[i].toString(16).padStart(6, "0");
      }
    }
    return encoded;
  }, []);

  // ─── Routing builder ──────────────────────────────────────────────────────
  //
  // All tokens swap directly to WETH.
  // Bypass WETH itself and GEMS (already native to the contract).

  const buildSwapRouting = useCallback(
    async ({
      tokenInAddress,
      purchaseAmountWei,
      minOutAmountWei,
      recipientAddress,
      chainId,
    }) => {
      const { weth, gems, v3Factory, v2Factory, v4StateView } =
        CHAIN_ADDRESSES[chainId] || CHAIN_ADDRESSES[1];

      const addr = tokenInAddress?.toLowerCase();

      if (addr === weth.toLowerCase() || addr === gems.toLowerCase()) {
        return { commands: "0x", inputs: [] };
      }

      // ─── ABI encoders ──────────────────────────────────────────────────────

      const encodeGetPool = (tA, tB, fee) =>
        web3.eth.abi.encodeFunctionCall(
          {
            name: "getPool",
            type: "function",
            inputs: [
              { type: "address", name: "tokenA" },
              { type: "address", name: "tokenB" },
              { type: "uint24", name: "fee" },
            ],
          },
          [tA, tB, fee],
        );

      const encodeGetPair = (tA, tB) =>
        web3.eth.abi.encodeFunctionCall(
          {
            name: "getPair",
            type: "function",
            inputs: [
              { type: "address", name: "tokenA" },
              { type: "address", name: "tokenB" },
            ],
          },
          [tA, tB],
        );

      const encodeLiquidity = () =>
        web3.eth.abi.encodeFunctionCall(
          {
            name: "liquidity",
            type: "function",
            inputs: [],
            outputs: [{ type: "uint128" }],
          },
          [],
        );

      const encodeGetReserves = () =>
        web3.eth.abi.encodeFunctionCall(
          {
            name: "getReserves",
            type: "function",
            inputs: [],
            outputs: [
              { name: "reserve0", type: "uint112" },
              { name: "reserve1", type: "uint112" },
              { name: "blockTimestampLast", type: "uint32" },
            ],
          },
          [],
        );

      const encodeV4Liquidity = (tA, tB, fee) => {
        const tickSpacing = FEE_TO_TICK_SPACING[fee] ?? 60;
        const [currency0, currency1] =
          tA.toLowerCase() < tB.toLowerCase() ? [tA, tB] : [tB, tA];
        const poolKeyEncoded = web3.eth.abi.encodeParameters(
          ["address", "address", "uint24", "int24", "address"],
          [currency0, currency1, fee, tickSpacing, ZERO_ADDRESS],
        );
        const poolId = web3.utils.keccak256(poolKeyEncoded);
        const callData = web3.eth.abi.encodeFunctionCall(
          {
            name: "getLiquidity",
            type: "function",
            inputs: [{ name: "poolId", type: "bytes32" }],
            outputs: [{ name: "liquidity", type: "uint128" }],
          },
          [poolId],
        );
        return {
          callData,
          poolKey: {
            currency0,
            currency1,
            fee,
            tickSpacing,
            hooks: ZERO_ADDRESS,
          },
          poolId,
        };
      };

      // ─── BATCH 1: All direct pool lookups — tokenIn → WETH ────────────────
      // V3: 4 fee tiers
      // V2: 1
      // V4: 4 fee tiers
      // Total: 9 calls → 1 RPC round-trip

      const batch1 = [];
      const meta1 = [];

      // V3 direct: tokenIn → WETH
      for (const fee of POOL_FEES) {
        batch1.push({
          target: v3Factory,
          callData: encodeGetPool(tokenInAddress, weth, fee),
        });
        meta1.push({ type: "v3_direct", fee });
      }

      // V2 direct: tokenIn → WETH
      batch1.push({
        target: v2Factory,
        callData: encodeGetPair(tokenInAddress, weth),
      });
      meta1.push({ type: "v2_direct" });

      // V4 direct: tokenIn → WETH
      for (const fee of POOL_FEES) {
        const { callData, poolKey, poolId } = encodeV4Liquidity(
          tokenInAddress,
          weth,
          fee,
        );
        batch1.push({ target: v4StateView, callData });
        meta1.push({ type: "v4_direct", fee, poolKey, poolId });
      }

      const results1 = await multicall(batch1);

      // ─── Parse batch 1 ─────────────────────────────────────────────────────

      const v3DirectPools = {}; // fee → poolAddress
      let v2DirectPair = null;
      const v4DirectPools = {}; // fee → { liquidity BN, poolKey, poolId }

      for (let i = 0; i < results1.length; i++) {
        const { success, returnData } = results1[i];
        if (!success || !returnData || returnData === "0x") continue;
        const m = meta1[i];

        if (m.type === "v3_direct" || m.type === "v2_direct") {
          let parsedAddr;
          try {
            parsedAddr = web3.eth.abi.decodeParameter("address", returnData);
          } catch {
            continue;
          }
          if (!parsedAddr || parsedAddr === ZERO_ADDRESS) continue;

          if (m.type === "v3_direct") v3DirectPools[m.fee] = parsedAddr;
          else if (m.type === "v2_direct") v2DirectPair = parsedAddr;
        } else {
          // V4 — liquidity decoded directly
          let liq;
          try {
            liq = web3.eth.abi.decodeParameter("uint128", returnData);
          } catch {
            continue;
          }
          const liquidityBN = web3.utils.toBN(liq);
          if (!liquidityBN.gt(web3.utils.toBN(0))) continue;

          v4DirectPools[m.fee] = {
            liquidity: liquidityBN,
            poolKey: m.poolKey,
            poolId: m.poolId,
          };
        }
      }

      // ─── BATCH 2: Liquidity for found V3/V2 addresses ─────────────────────

      const batch2 = [];
      const meta2 = [];

      for (const [fee, poolAddr] of Object.entries(v3DirectPools)) {
        batch2.push({ target: poolAddr, callData: encodeLiquidity() });
        meta2.push({ type: "v3_direct", fee: Number(fee), address: poolAddr });
      }
      if (v2DirectPair) {
        batch2.push({ target: v2DirectPair, callData: encodeGetReserves() });
        meta2.push({ type: "v2_direct", address: v2DirectPair });
      }

      // fee → { liquidity: BN, address: string }
      const v3DirectLiq = {};
      let v2DirectLiq = null;

      if (batch2.length > 0) {
        const results2 = await multicall(batch2);

        for (let i = 0; i < results2.length; i++) {
          const { success, returnData } = results2[i];
          if (!success || !returnData || returnData === "0x") continue;
          const m = meta2[i];

          if (m.type === "v3_direct") {
            let liq;
            try {
              liq = web3.eth.abi.decodeParameter("uint128", returnData);
            } catch {
              continue;
            }
            const bn = web3.utils.toBN(liq);
            if (!bn.gt(web3.utils.toBN(0))) continue;
            v3DirectLiq[m.fee] = { liquidity: bn, address: m.address };
          } else {
            // V2 reserves
            let r0, r1;
            try {
              const dec = web3.eth.abi.decodeParameters(
                [
                  { type: "uint112", name: "reserve0" },
                  { type: "uint112", name: "reserve1" },
                  { type: "uint32", name: "blockTimestampLast" },
                ],
                returnData,
              );
              r0 = web3.utils.toBN(dec.reserve0);
              r1 = web3.utils.toBN(dec.reserve1);
            } catch {
              continue;
            }
            if (!r0.gt(web3.utils.toBN(0)) || !r1.gt(web3.utils.toBN(0)))
              continue;

            // min(r0, r1) as liquidity proxy for cross-version comparison
            const proxy = r0.lt(r1) ? r0 : r1;
            v2DirectLiq = { liquidity: proxy, address: m.address };
          }
        }
      }

      // ─── Build candidates ──────────────────────────────────────────────────

      const candidates = [];

      // V3 direct: tokenIn → WETH
      for (const [fee, data] of Object.entries(v3DirectLiq)) {
        const path = encodeV3Path([tokenInAddress, Number(fee), weth]);
        candidates.push({
          liquidity: data.liquidity,
          label: `V3 direct fee=${fee}`,
          poolAddresses: { direct: data.address },
          commands: "0x" + COMMANDS.V3_SWAP_EXACT_IN,
          inputs: [
            web3.eth.abi.encodeParameters(
              ["address", "uint256", "uint256", "bytes", "bool"],
              [
                recipientAddress,
                purchaseAmountWei,
                minOutAmountWei,
                path,
                true,
              ],
            ),
          ],
        });
      }

      // V4 direct: tokenIn → WETH
      for (const [fee, { liquidity, poolKey, poolId }] of Object.entries(
        v4DirectPools,
      )) {
        const v4Planner = new V4Planner();
        const routePlanner = new RoutePlanner();
        v4Planner.addAction(Actions.SWAP_EXACT_IN_SINGLE, [
          {
            poolKey,
            zeroForOne:
              tokenInAddress.toLowerCase() === poolKey.currency0.toLowerCase(),
            amountIn: purchaseAmountWei.toString(),
            amountOutMinimum: minOutAmountWei.toString(),
            hookData: "0x",
          },
        ]);
        v4Planner.addAction(Actions.SETTLE_ALL, [
          tokenInAddress,
          purchaseAmountWei.toString(),
        ]);
        v4Planner.addAction(Actions.TAKE_ALL, [
          weth,
          minOutAmountWei.toString(),
        ]);
        const encodedActions = v4Planner.finalize();
        routePlanner.addCommand(CommandType.V4_SWAP, [
          v4Planner.actions,
          v4Planner.params,
        ]);
        candidates.push({
          liquidity,
          label: `V4 direct fee=${fee}`,
          poolAddresses: { direct: poolId },
          commands: routePlanner.commands,
          inputs: [encodedActions],
        });
      }

      // V2 direct: tokenIn → WETH
      if (v2DirectLiq) {
        candidates.push({
          liquidity: v2DirectLiq.liquidity,
          label: "V2 direct",
          poolAddresses: { direct: v2DirectLiq.address },
          commands: "0x" + COMMANDS.V2_SWAP_EXACT_IN,
          inputs: [
            web3.eth.abi.encodeParameters(
              ["address", "uint256", "uint256", "address[]", "bool"],
              [
                recipientAddress,
                purchaseAmountWei,
                minOutAmountWei,
                [tokenInAddress, weth],
                true,
              ],
            ),
          ],
        });
      }

      // ─── Finding highest-liquidity route ──────────────────────────────────────

      if (candidates.length === 0) {
        console.warn("[routing] No route found for", tokenInAddress);
        return { commands: "0x", inputs: [] };
      }

      const best = candidates.reduce((a, b) =>
        b.liquidity.gt(a.liquidity) ? b : a,
      );

      console.log(
        "[routing] Best route:",
        best.label,
        "liquidity:",
        best.liquidity.toString(),
        "poolAddresses:",
        best.poolAddresses,
        "tokenIn:",
        tokenInAddress,
      );

      return { commands: best.commands, inputs: best.inputs };
    },
    [web3, multicall, encodeV3Path],
  );

  // ─── Main purchase function ────────────────────────────────────────────────

  const purchaseNFTwithUsdc = useCallback(
    async (
      postApiRes,
      currentRound,
      nftPriceArray,
      price,
      nftPriceArray1,
      balance,
      index,
      checkBox,
    ) => {
      if (!account) throw new Error("WALLET_NOT_CONNECTED");

      // Convert USDC amount to mwei
      let amount = nftPriceArray1 / price;
      amount = parseFloat(amount).toFixed(6);
      const valueInWei = web3.utils.toWei(amount.toString(), "mwei");

      console.log("NFT USDC purchase params:", {
        UsdcAddress,
        nftPriceArray,
        valueInWei,
      });

      // Build Universal Router commands + inputs
      let swapCommands = "0x";
      let swapInputs = [];

      try {
        const routing = await buildSwapRouting({
          tokenInAddress: UsdcAddress,
          purchaseAmountWei: valueInWei,
          minOutAmountWei: "0", // tighten with slippage calc if needed
          recipientAddress: Environment?.buyBackAndBurn,
          chainId: chainId ?? 1,
        });

        swapCommands = routing.commands;
        swapInputs = routing.inputs;

        console.log("[usePurchaseToken] routing →", {
          commands: swapCommands,
          inputs: swapInputs,
        });
      } catch (routingErr) {
        console.warn(
          "[usePurchaseToken] routing failed, proceeding with empty:",
          routingErr,
        );
      }

      try {
        return new Promise(async (resolve, reject) => {
          const args = isFirstContractVersion
            ? [
                UsdcAddress,
                postApiRes.price,
                postApiRes.normalizationFactorToken,
                postApiRes.code,
                currentRound,
                nftPriceArray,
                postApiRes.deadline,
                index,
                postApiRes.agentsAddresses,
                postApiRes.agentsPercentages,
                swapInputs, // inputs  (bytes[])
                swapCommands, // commands (bytes)
                postApiRes.v,
                postApiRes.r,
                postApiRes.s,
              ]
            : [
                UsdcAddress,
                postApiRes.price,
                postApiRes.normalizationFactorToken,
                postApiRes.normalizationFactorNft,
                postApiRes.code,
                currentRound,
                nftPriceArray,
                postApiRes.deadline,
                index,
                postApiRes.agentsAddresses,
                postApiRes.agentsPercentages,
                swapInputs, // inputs  (bytes[])
                swapCommands, // commands (bytes)
                postApiRes.v,
                postApiRes.r,
                postApiRes.s,
              ];

          console.log("BUYING NFT WITH USDC PARAMS:", args);

          let gas;

          try {
            let gasPrice = await web3.eth.getGasPrice();
            gasPrice = parseInt(gasPrice) + 2000000000;

            gas = await contract.methods.purchaseNFTWithToken
              .apply(contract.methods, args)
              .estimateGas({ from: account, gasPrice });
          } catch (err) {
            return reject(err);
          }

          const gasLimit = Math.floor(gas * 1.2);

          const gasConfig = await getTxGasConfig(web3, 1, account);

          console.log("Estimated gas for purchaseNFTWithToken:", {
            gas,
            gasLimit,
            ...gasConfig,
          });

          contract.methods.purchaseNFTWithToken
            .apply(contract.methods, args)
            .send({ from: account, gas: gasLimit, ...gasConfig })
            .on("transactionHash", (hash) => {
              console.log("NFT USDC tx hash:", hash);
            })
            .on("receipt", (receipt) => {
              console.log("NFT USDC purchase confirmed:", receipt);
              resolve({
                transactionHash: receipt?.transactionHash,
                receipt,
              });
            })
            .on("error", (error) => {
              console.error("NFT USDC purchase error:", error);
              reject(error);
            });
        });
      } catch (error) {
        throw error;
      }
    },
    [
      account,
      contract,
      web3,
      isFirstContractVersion,
      UsdcAddress,
      buildSwapRouting,
      chainId,
    ],
  );

  return { purchaseNFTwithUsdc };
};

export default usePurchaseNFTwithUsdc;
